import logger from '../../../utils/logger';
/**
 * Settlement Email Helper
 * Settlement-specific email functionality using the generalized email service
 */

import { useGeneralizedEmailPreview } from '../../../hooks/useGeneralizedEmailPreview';
import { generateEmailPreview, sendEmail, EMAIL_TEMPLATE_TYPES } from '../EmailFactory/emailService';
import axiosInstance from '../../../fetchConfig/axiosConfig';
import { usePersistedDataStore } from '../../store';
import { getAVUtil } from '../../../utils/getAVUtil';
import { generateStipulationsForSettledRows } from './downloadStips.js';
import { getSettlementStageForRow } from './settlementStageUtils.js';
import { uploadSettlementXls } from './settlementXls.js';
import { useUser } from '../../UserContext.js';
import { toMySQLDateTime } from '../../../common/helper_functions.js';

/**
 * Check if a case is settled
 * @param {Object} row - Table row data
 * @returns {boolean} Whether the case is settled
 */
const isCaseSettled = (row) => {
  return row._isSettled || row.statusDisplay === 'Settled';
};

/**
 * Convert settlement data for email display
 * @param {Array} settlements - Raw settlement data from table
 * @returns {Array} Formatted settlement data for email
 */
const convertSettlementsForEmailDisplay = (settlements) => {
  if (!Array.isArray(settlements)) return [];
  
  // Get negotiationObj from persisted state
  const negotiationObj = usePersistedDataStore.getState().negotiationObj;
  
  return settlements.map(settlement => {
    logger.log('🔍 [SETTLEMENT EMAIL] Processing settlement:', settlement);
    logger.log('🔍 [SETTLEMENT EMAIL] Available fields:', Object.keys(settlement));
    
    const {
      parcel_id,
      Address,
      SettlementValue,
      OriginalAssessment,
      PropertyAssessment,
      AssessmentVillage,
      Stage,
      Action,
      SettlementDate,
      ...otherFields
    } = settlement;
    
    // Get the case object from negotiationObj.cases by matching parcel_id
    const caseObject = negotiationObj?.cases?.find(c => c.parcel_id === parcel_id) || null;
    
    // Use getAVUtil with case object, otherwise fall back to provided values
    const assessmentValue = OriginalAssessment || getAVUtil(caseObject);
    
    return {
      ParcelID: parcel_id,
      Address: Address || 'Unknown Address',
      SettlementValue: SettlementValue || 0,
      OriginalAssessment: assessmentValue,
      Stage: Stage || 'Unknown',
      Action: Action || 'Unknown',
      SettlementDate: SettlementDate || null,
      ...otherFields
    };
  });
};

/**
 * Sanitize settlement data for email transmission
 * @param {Array} settlements - Settlement data to sanitize
 * @returns {Array} Sanitized settlement data
 */
const sanitizeSettlementsForEmail = (settlements) => {
  if (!Array.isArray(settlements)) return [];
  
  return settlements.map(settlement => {
    if (!settlement || typeof settlement !== 'object') return settlement;
    
    // Create a clean copy without functions, undefined values, etc.
    const sanitized = {};
    for (const [key, value] of Object.entries(settlement)) {
      if (value !== undefined && typeof value !== 'function') {
        // Skip React components and circular references
        if (value && typeof value === 'object' && value.$$typeof) {
          continue; // Skip React elements
        }
        if (value && typeof value === 'object' && value.constructor && value.constructor.name === 'FiberNode') {
          continue; // Skip React Fiber nodes
        }
        sanitized[key] = value;
      }
    }
    return sanitized;
  });
};

/**
 * Sanitize user object for email transmission
 * @param {Object} user - User object to sanitize
 * @returns {Object} Sanitized user object
 */
const sanitizeUserForEmail = (user) => {
  if (!user || typeof user !== 'object') {
    return { userName: 'Aventine', email: '', userId: null };
  }
  
  return {
    userName: user.userName || 'Aventine',
    email: user.email || '',
    userId: user.userId || null,
    organization: user.organization || 'Propriety'
  };
};

/**
 * Sanitize case info for email transmission
 * @param {Object} caseInfo - Case info object to sanitize
 * @returns {Object} Sanitized case info object
 */
const sanitizeCaseInfoForEmail = (caseInfo) => {
  if (!caseInfo || typeof caseInfo !== 'object') {
    return { totalCases: 0, openCases: 0, settledCases: 0 };
  }
  
  return {
    totalCases: caseInfo.totalCases || 0,
    openCases: caseInfo.openCases || 0,
    settledCases: caseInfo.settledCases || 0
  };
};

/**
 * Deep sanitize any object to remove circular references and React components
 * @param {any} obj - Object to sanitize
 * @param {Set} seen - Set of seen objects to prevent circular references
 * @returns {any} Sanitized object
 */
const deepSanitize = (obj, seen = new Set()) => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  
  // Check for circular reference
  if (seen.has(obj)) {
    return '[Circular Reference]';
  }
  
  // Check for React components
  if (obj.$$typeof || (obj.constructor && obj.constructor.name === 'FiberNode')) {
    return '[React Component]';
  }
  
  seen.add(obj);
  
  if (Array.isArray(obj)) {
    return obj.map(item => deepSanitize(item, seen));
  }
  
  const sanitized = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value !== 'function') {
      sanitized[key] = deepSanitize(value, seen);
    }
  }
  
  seen.delete(obj);
  return sanitized;
};

/**
 * Generate stipulations for email send
 * @param {Array} settledRows - Array of settled case rows
 * @param {Object} negotiationObj - Negotiation object with context
 * @returns {Promise<Object>} Result with success status and file_url
 */
const generateStipulationsForEmail = async (settledRows, negotiationObj) => {
  if (!settledRows?.length) {
    return { success: false, error: 'No parcel IDs found in settled rows' };
  }
  return generateStipulationsForSettledRows({ settledRows, negotiationObj, shouldDownload: false });
};

/**
 * Hook for settlement email functionality
 * @returns {Object} Settlement email hook functions and components
 */
export const useSettlementEmail = () => {
  const emailPreviewHook = useGeneralizedEmailPreview();
  const { user } = useUser();
  /**
   * Show settlement email preview dialog
   * @param {Object} params - Email preview parameters
   * @param {Array} params.selectedRows - Selected table rows
   * @param {Array} params.allRows - All table rows
   * @param {Object} params.negotiationObj - Negotiation object
   * @param {Function} params.onComplete - Completion callback
   * @param {boolean} [params.includeXls] - Internal users: attach a stripped settlement XLS
   * @returns {Promise<void>}
   */
  const showSettlementEmailPreview = async (params) => {
    
    const {
      selectedRows,
      allRows,
      negotiationObj,
      onComplete,
      includeXls = false
    } = params;



    try {
      // Filter to only settled cases
      const settledRows = selectedRows.filter(row => isCaseSettled(row));
      
      if (settledRows.length === 0) {
        logger.error('❌ [SETTLEMENT EMAIL HELPER] No settled cases found');
        throw new Error('No settled cases selected. Please select cases that have been settled.');
      }


      // Calculate case statistics
      const totalCases = allRows.length;
      const settledCases = allRows.filter(row => isCaseSettled(row)).length;
      const openCases = totalCases - settledCases;

      const caseInfo = {
        totalCases,
        openCases,
        settledCases
      };

      // Convert settlements to email format
      logger.log('🔍 [SETTLEMENT EMAIL] Raw settled rows:', settledRows);
      const settlementsForEmail = convertSettlementsForEmailDisplay(settledRows);
      logger.log('🔍 [SETTLEMENT EMAIL] Converted settlements for email:', settlementsForEmail);

      // Generate stipulations BEFORE showing email preview
      let stipulationsUrl = null;
      try {
        logger.log('🔧 [SETTLEMENT EMAIL] Generating stipulations for email preview...');
        const stipulationsResult = await generateStipulationsForEmail(settledRows, negotiationObj);
        if (stipulationsResult.success && stipulationsResult.file_url) {
          stipulationsUrl = stipulationsResult.file_url;
          logger.log('✅ [SETTLEMENT EMAIL] Stipulations generated successfully:', stipulationsUrl);
        } else {
          logger.warn('⚠️ [SETTLEMENT EMAIL] Stipulations generation failed:', stipulationsResult.error);
        }
      } catch (stipError) {
        logger.error('❌ [SETTLEMENT EMAIL] Error generating stipulations:', stipError);
        // Continue with email preview even if stipulations fail
      }

      let settlementXls = null;
      if (includeXls) {
        settlementXls = await uploadSettlementXls(settledRows);
        logger.log('✅ [SETTLEMENT EMAIL] Settlement XLS uploaded:', settlementXls.csvFilename);
      }

      // Sanitize all data for email transmission
      const sanitizedSettlements = sanitizeSettlementsForEmail(settlementsForEmail);
      const sanitizedCaseInfo = sanitizeCaseInfoForEmail(caseInfo);

      // Prepare email preview options
      logger.log('📝 [SETTLEMENT EMAIL HELPER] Preparing email preview options');
      const emailPreviewOptions = {
        templateType: EMAIL_TEMPLATE_TYPES.SETTLEMENTS,
        data: sanitizedSettlements,
        caseInfo: sanitizedCaseInfo,
        stipulationsUrl: stipulationsUrl
      };
      logger.log('📝 [SETTLEMENT EMAIL HELPER] Email preview options prepared:', emailPreviewOptions);

      // Final deep sanitization as safety net
      const finalSanitizedOptions = deepSanitize(emailPreviewOptions);

      
      await emailPreviewHook.showDialog(finalSanitizedOptions, async (confirmed, emailData) => {
        if (confirmed) {
          try {
            const emailResult = await sendEmail({
              templateType: EMAIL_TEMPLATE_TYPES.SETTLEMENTS,
              data: settlementsForEmail,
              fileUrl: stipulationsUrl,
              xlsDownloadUrl: settlementXls?.downloadUrl,
              xlsFilename: settlementXls?.csvFilename,
              toEmails: emailData?.toEmails || [],
              ccEmails: emailData?.ccEmails || [],
              bccEmails: emailData?.bccEmails || [],
              subject: emailData?.subject,
              emailBody: emailData?.emailBody,
              caseInfo: emailData?.caseInfo,
              senderEmail: 'stipulations@aventine.ai',
              senderName: 'Stipulations'
            });
            
            // Update stipulation status in frontend only (not in database) - SO STUPID that we need this.
            if (emailResult.success) {
              // Extract parcel IDs from settled rows
              const parcelIds = settledRows.map(row => row.parcel_id).filter(Boolean);
              
              if (parcelIds.length > 0) {
                const getNegotiationObj = usePersistedDataStore.getState().negotiationObj;
                const setNegotiationObj = usePersistedDataStore.getState().setNegotiationObj;
                const setNegotiationObjVersion = usePersistedDataStore.getState().setNegotiationObjVersion;
                
                if (getNegotiationObj && setNegotiationObj && setNegotiationObjVersion) {
                  // Create a deep copy of the negotiation object
                  const updatedNegotiationObj = JSON.parse(JSON.stringify(getNegotiationObj));
                  
                  // Update stipulation status for each case (frontend only)
                  settledRows.forEach((row) => {
                    const parcelId = row.parcel_id;
                    if (!parcelId) return;
                    const stage = getSettlementStageForRow(row, negotiationObj);
                    if (stage === 'Art7') return;
                    const stipInfoKey = stage === 'BAR' ? 'bar' : 'scar';
                    const caseIndex = updatedNegotiationObj.cases.findIndex(c => c.parcel_id === parcelId);
                    if (caseIndex !== -1) {
                      const priorCase = updatedNegotiationObj.cases[caseIndex];
                      const priorStipInfo = priorCase.StipInfo && typeof priorCase.StipInfo === 'object' ? priorCase.StipInfo : {};
                      updatedNegotiationObj.cases[caseIndex] = {
                        ...priorCase,
                        StipInfo: {
                          ...priorStipInfo,
                          [stipInfoKey]: {
                            status: 'SentForCounterSig',
                            negotiator: user?.userId ?? priorStipInfo[stipInfoKey]?.negotiator ?? null,
                            lastUpdated: toMySQLDateTime(new Date()),
                          },
                        },
                      };
                    }
                  });
                  
                  // Update the store
                  setNegotiationObj(updatedNegotiationObj);
                  setNegotiationObjVersion(prev => prev + 1);
                  
                  logger.log(`✅ [SETTLEMENT EMAIL] Frontend stipulation status set to "SentForCounterSig" for ${parcelIds.length} parcel(s)`);
                }
              }
            }
            
            onComplete(emailResult.success, emailResult.message || emailResult.error);
          } catch (error) {
            onComplete(false, error.message);
          }
        } else {
          onComplete(false, 'Email cancelled by user');
        }
      });

    } catch (error) {
      logger.error('❌ [SETTLEMENT EMAIL HELPER] Error in settlement email preview:', error);
      if (onComplete) {
        onComplete(false, { error: error.message });
      }
    }
  };

  const returnObject = {
    showSettlementEmailPreview,
    SettlementEmailDialog: emailPreviewHook.dialogComponent
  };
  
  return returnObject;
};
